import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  LinearProgress,
  Tooltip
} from '@mui/material';
import { courses } from '../../services/api';

interface FlowStat {
  id: number;
  name: string;
  distinct_users_completed: number;
}

interface LifePlanStatsData {
  course_id: string;
  organization_id: string;
  total_org_users: number;
  results: FlowStat[];
}

interface LifePlanCompletionStatsProps {
  organizationId?: string;
}

const LifePlanCompletionStats: React.FC<LifePlanCompletionStatsProps> = ({ organizationId }) => {
  const [statsData, setStatsData] = useState<LifePlanStatsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStatsData();
  }, [organizationId]);

  const fetchStatsData = async () => {
    try {
      setLoading(true);
      const data = await courses.getLifePlanCompletionStats(organizationId);
      setStatsData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching life plan completion stats:', err);
      setError('Failed to fetch life plan completion statistics. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const getProgressColor = (percentage: number) => {
    if (percentage < 30) return 'error';
    if (percentage < 70) return 'warning';
    return 'success';
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" gutterBottom>
            Life Plan Completion Data
          </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : statsData && statsData.results.length > 0 ? (
              <Box>
                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Total Users in Organization:</strong> {statsData.total_org_users}
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Flow Name</TableCell>
                        <TableCell>Completion</TableCell>
                        <TableCell align="right">Users</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {statsData.results.map((row) => {
                        const completionPercentage = statsData.total_org_users > 0 
                          ? Math.round((row.distinct_users_completed / statsData.total_org_users) * 100) 
                          : 0;
                        return (
                          <TableRow key={row.name}>
                            <TableCell>{row.name}</TableCell>
                            <TableCell sx={{ width: '50%' }}>
                              <Tooltip title={`${completionPercentage}% of users completed this flow`}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box sx={{ width: '100%', mr: 1 }}>
                                    <LinearProgress 
                                      variant="determinate" 
                                      value={completionPercentage} 
                                      color={getProgressColor(completionPercentage)}
                                      sx={{ height: 10, borderRadius: 5 }}
                                    />
                                  </Box>
                                  <Box sx={{ minWidth: 35 }}>
                                    <Typography variant="body2" color="text.secondary">{`${completionPercentage}%`}</Typography>
                                  </Box>
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">{row.distinct_users_completed}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Alert severity="info">No flow completion data available.</Alert>
            )}
          </Paper>
        </Grid>
      </Grid>
  );
};

export default LifePlanCompletionStats;
